import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostingService from '../../services/PostingService';
import { taskErrorCode, taskErrorMessage } from '../../services/TaskTrackingService';
import { shortDay } from './calendarUi';

/**
 * Mark ready to post (rev6 Part 11, 6j-4): the video's completed languages, all ticked. Confirming
 * emails AV once. Unticking a ready language takes it back, with no email.
 */
const MarkReadyDialog = ({ taskId, onClose, onSaved }) => {
    useTrackUsage('MarkReadyDialog', 'dialog', 'posts');
    const [languages, setLanguages] = useState(null);
    const [picked, setPicked] = useState([]);
    const [needsDate, setNeedsDate] = useState('');
    const [date, setDate] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let live = true;
        PostingService.readyLanguages(taskId)
            .then((list) => {
                if (!live) return;
                setLanguages(list);
                setPicked(list.map((l) => l.languageKey));
            })
            .catch((e) => live && setError(taskErrorMessage(e)));
        return () => {
            live = false;
        };
    }, [taskId]);

    const toggle = (key, on) => setPicked((p) => (on ? [...p, key] : p.filter((k) => k !== key)));

    const save = async () => {
        setBusy(true);
        setError('');
        try {
            await PostingService.setReady(taskId, picked, date);
            onSaved();
        } catch (e) {
            if (taskErrorCode(e) === 'NEEDS_DATE') setNeedsDate(taskErrorMessage(e));
            else setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Mark ready to post</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 1 }}>Check the title, thumbnail and video link first. AV gets one email for the languages you pick.</DialogContentText>
                {languages?.map((l) => (
                    <FormControlLabel
                        key={l.languageKey}
                        sx={{ display: 'flex' }}
                        control={<Checkbox checked={picked.includes(l.languageKey)} onChange={(e) => toggle(l.languageKey, e.target.checked)} />}
                        label={
                            <Typography variant="body2">
                                {l.languageLabel}
                                {l.date ? ` · ${shortDay(l.date)}` : ' · no posting date'}
                                {l.posted ? ' · Posted' : ''}
                            </Typography>
                        }
                    />
                ))}
                {needsDate && (
                    <>
                        <Alert severity="info" sx={{ my: 1 }}>
                            {needsDate}
                        </Alert>
                        <TextField type="date" fullWidth label="Posting date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
                    </>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={busy || !languages || (!!needsDate && !date)} onClick={save}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkReadyDialog;
